/**
 * Serverzustand des Anmeldemoduls.
 *
 * docs/02, Abschnitt 4: "Serverzustand ausschliesslich ueber TanStack Query.
 * Keine Kopien von Serverdaten in lokalem State." Wer den angemeldeten Nutzer
 * zusaetzlich in einem eigenen Zustand haelt, hat zwei Wahrheiten -- und die
 * zweite ist nach dem naechsten Mandantenwechsel veraltet.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import { clearDrafts } from '../../lib/draft';
import type { AbsenceUpdate } from './api';
import {
  activateMfa,
  beginMfaSetup,
  changePassword,
  disableMfa,
  fetchNotificationPreferences,
  fetchSession,
  setAbsence,
  setNotificationPreference,
  signIn,
  signOut,
  switchTenant,
} from './api';

export const sessionQueryKey = ['session'] as const;

export function useSession() {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: fetchSession,
    // Ein abgelaufenes Token wird vom Client selbst erneuert. Ein 401, das
    // bis hierher durchkommt, heisst: Die Sitzung ist wirklich zu Ende --
    // eine Wiederholung braechte nur ein zweites 401.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.isPermanent) && failureCount < 2,
    staleTime: 60_000,
  });
}

export function useSignIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      email,
      password,
      totpCode,
    }: {
      email: string;
      password: string;
      totpCode?: string;
    }) => signIn(email, password, totpCode),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}

/**
 * Die Einrichtung des zweiten Faktors -- F5-16.
 *
 * Das Ergebnis bleibt im Mutationszustand und wandert **nicht** in den
 * Query-Cache: Es enthaelt das Geheimnis im Klartext, und ein Cache ist ein
 * Ort, an dem Dinge laenger liegen, als man denkt.
 */
export function useBeginMfaSetup() {
  return useMutation({ mutationFn: (password: string) => beginMfaSetup(password) });
}

export function useActivateMfa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => activateMfa(code),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}

export function useDisableMfa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { password: string; code: string }) =>
      disableMfa(variables.password, variables.code),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}

export const notificationPreferencesKey = ['notification-preferences'] as const;

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationPreferencesKey,
    queryFn: fetchNotificationPreferences,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.isPermanent) && failureCount < 2,
  });
}

export function useSetNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { event: string; by_email: boolean }) =>
      setNotificationPreference(variables.event, variables.by_email),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: notificationPreferencesKey });
    },
  });
}

export function useSetAbsence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AbsenceUpdate) => setAbsence(payload),
    onSuccess: async () => {
      // Die Nutzerliste traegt den Abwesenheitshinweis an jedem Eintrag.
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signOut,
    onSettled: () => {
      // Der gesamte zwischengespeicherte Serverzustand gehoert zur beendeten
      // Sitzung. Bliebe er stehen, saehe der naechste Anmeldevorgang fuer
      // einen Augenblick die Tickets des vorigen Nutzers -- moeglicherweise
      // aus einem anderen Mandanten.
      queryClient.clear();
      // Dasselbe gilt fuer unversendete Beitragstexte: Sie koennen
      // personenbezogene Daten enthalten und haben an einem geteilten
      // Arbeitsplatz nach der Abmeldung nichts mehr zu suchen.
      clearDrafts();
    },
  });
}

export function useSwitchTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: switchTenant,
    onSuccess: () => {
      // Nach dem Mandantenwechsel ist jeder zwischengespeicherte Datensatz
      // aus dem falschen Mandanten. Nicht ungueltig machen, sondern
      // wegwerfen: Ungueltige Daten wuerden bis zum Nachladen weiter
      // angezeigt.
      queryClient.clear();
    },
  });
}

/**
 * Der eigene Kennwortwechsel.
 *
 * Ohne Ungueltigmachen der Sitzungsabfrage: Der Server hat gerade **alle**
 * Anmeldungen beendet, auch diese. Ein Nachladen liefe damit in den
 * Erneuerungsweg und meldete den Nutzer ab, bevor er die Bestaetigung
 * gelesen hat. Er meldet sich beim naechsten Klick ohnehin neu an.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (payload: { current_password: string; new_password: string }) =>
      changePassword(payload),
  });
}
